import { useEffect, useRef, useCallback } from 'react';
import type { TelegramAuthData } from '@/utils/auth-pb';

const BOT_USERNAME = import.meta.env.VITE_TELEGRAM_BOT_USERNAME as string;

interface TelegramLoginButtonProps {
  onAuth: (data: TelegramAuthData) => void;
  label?: string;
  size?: 'large' | 'medium';
}

declare global {
  interface Window {
    onTelegramAuth?: (data: TelegramAuthData) => void;
  }
}

function TelegramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
    </svg>
  );
}

export function TelegramLoginButton({
  onAuth,
  label = 'Войти через Telegram',
  size = 'large',
}: TelegramLoginButtonProps) {
  const widgetRef = useRef<HTMLDivElement>(null);

  const handleAuth = useCallback(
    (data: TelegramAuthData) => {
      onAuth(data);
    },
    [onAuth]
  );

  useEffect(() => {
    if (!BOT_USERNAME) {
      console.warn('VITE_TELEGRAM_BOT_USERNAME is not set');
      return;
    }

    // Register global callback
    window.onTelegramAuth = handleAuth;

    // Inject Telegram widget script (invisible, handles auth popup)
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', BOT_USERNAME);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.setAttribute('data-request-access', 'write');
    script.async = true;

    if (widgetRef.current) {
      widgetRef.current.innerHTML = '';
      widgetRef.current.appendChild(script);
    }

    return () => {
      delete window.onTelegramAuth;
      if (widgetRef.current) {
        widgetRef.current.innerHTML = '';
      }
    };
  }, [handleAuth]);

  if (!BOT_USERNAME) {
    return null;
  }

  const isLarge = size === 'large';

  return (
    <div className="relative w-full">
      {/* Custom styled button (visible) */}
      <div
        className={`flex items-center justify-center gap-[10px] w-full rounded-[12px] bg-[#54a9eb] cursor-pointer transition-colors hover:bg-[#4a9ae0] ${
          isLarge ? 'h-[52px]' : 'h-[44px]'
        }`}
      >
        <TelegramIcon className={`text-white ${isLarge ? 'h-[22px] w-[22px]' : 'h-[18px] w-[18px]'}`} />
        <span
          className={`font-segoe font-semibold text-white ${
            isLarge ? 'text-[16px]' : 'text-[14px]'
          }`}
        >
          {label}
        </span>
      </div>

      {/* Invisible Telegram widget overlay — captures clicks, opens auth popup */}
      <div
        ref={widgetRef}
        className="absolute inset-0 [&_iframe]:absolute [&_iframe]:top-1/2 [&_iframe]:left-1/2 [&_iframe]:-translate-x-1/2 [&_iframe]:-translate-y-1/2 [&_iframe]:scale-[3]"
        style={{ opacity: 0 }}
      />
    </div>
  );
}
